<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import ContactDetailsItem from './ContactDetailsItem.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import ConversationLabels from './labels/LabelBox.vue';
import { CONVERSATION_PRIORITY } from '../../../../shared/constants/messages';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BoardsAPI from 'dashboard/api/boards';

export default {
  components: {
    ContactDetailsItem,
    MultiselectDropdown,
    ConversationLabels,
    NextButton,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList();
    return {
      agentsList,
    };
  },
  data() {
    return {
      isLoadingKanbanLead: false,
      isSavingKanbanLead: false,
      kanbanBoard: null,
      kanbanDeal: null,
      kanbanStages: [],
      priorityOptions: [
        {
          id: null,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
          icon: 'i-woot-priority-empty',
        },
        {
          id: CONVERSATION_PRIORITY.URGENT,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
          icon: 'i-woot-priority-urgent',
        },
        {
          id: CONVERSATION_PRIORITY.HIGH,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
          icon: 'i-woot-priority-high',
        },
        {
          id: CONVERSATION_PRIORITY.MEDIUM,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
          icon: 'i-woot-priority-medium',
        },
        {
          id: CONVERSATION_PRIORITY.LOW,
          name: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
          icon: 'i-woot-priority-low',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      teams: 'teams/getTeams',
    }),
    hasAnAssignedTeam() {
      return !!this.currentChat?.meta?.team;
    },
    teamsList() {
      if (this.hasAnAssignedTeam) {
        return [
          { id: 0, name: this.$t('TEAMS_SETTINGS.LIST.NONE') },
          ...this.teams,
        ];
      }
      return this.teams;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : null;
        this.$store.dispatch('setCurrentChatAssignee', {
          conversationId: this.currentChat.id,
          assignee: agent,
        });
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    assignedTeam: {
      get() {
        return this.currentChat.meta.team;
      },
      set(team) {
        const conversationId = this.currentChat.id;
        const teamId = team ? team.id : 0;
        this.$store.dispatch('setCurrentChatTeam', { team, conversationId });
        this.$store
          .dispatch('assignTeam', { conversationId, teamId })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_TEAM'));
          });
      },
    },
    assignedPriority: {
      get() {
        const selectedOption = this.priorityOptions.find(
          opt => opt.id === this.currentChat.priority
        );

        return selectedOption || this.priorityOptions[0];
      },
      set(priorityItem) {
        const conversationId = this.currentChat.id;
        const oldValue = this.currentChat?.priority;
        const priority = priorityItem ? priorityItem.id : null;

        this.$store.dispatch('setCurrentChatPriority', {
          priority,
          conversationId,
        });
        this.$store
          .dispatch('assignPriority', { conversationId, priority })
          .then(() => {
            useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
              oldValue,
              newValue: priority,
              from: 'Conversation Sidebar',
            });
            useAlert(
              this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
                priority: priorityItem.name,
                conversationId,
              })
            );
          });
      },
    },
    showSelfAssign() {
      if (!this.assignedAgent) {
        return true;
      }
      if (this.assignedAgent.id !== this.currentUser.id) {
        return true;
      }
      return false;
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    contactName() {
      const sender = this.currentChat?.meta?.sender || {};
      return (
        sender.name ||
        sender.email ||
        sender.phone_number ||
        this.$t('CONVERSATION.CRM.DEFAULT_LEAD_NAME')
      );
    },
    conversationDisplayId() {
      return this.currentChat?.display_id || this.currentChat?.id;
    },
    leadStageOptions() {
      return this.kanbanStages.map(stage => ({
        id: stage.id,
        name: stage.name,
      }));
    },
    selectedLeadStage() {
      if (!this.kanbanDeal) return null;
      return (
        this.leadStageOptions.find(
          stage => stage.id === this.kanbanDeal.stage_id
        ) || null
      );
    },
    hasKanbanBoard() {
      return !!this.kanbanBoard;
    },
    hasKanbanDeal() {
      return !!this.kanbanDeal;
    },
    dealAmount() {
      return this.formatCurrency(this.kanbanDeal?.amount_cents || 0);
    },
    currentStageName() {
      return (
        this.kanbanStages.find(stage => stage.id === this.kanbanDeal?.stage_id)
          ?.name || this.$t('CONVERSATION.CRM.UNKNOWN_STAGE')
      );
    },
    dealAssigneeName() {
      return (
        this.kanbanDeal?.assignee?.name ||
        this.currentChat?.meta?.assignee?.name ||
        this.$t('CONVERSATION.CRM.UNASSIGNED')
      );
    },
    expectedCloseDateLabel() {
      return this.formatDate(this.kanbanDeal?.expected_close_date);
    },
    primaryLinkedConversation() {
      return this.kanbanDeal?.conversations?.[0] || null;
    },
    linkedConversationStatus() {
      return (
        this.primaryLinkedConversation?.status ||
        this.currentChat?.status ||
        this.$t('CONVERSATION.CRM.UNKNOWN_STATUS')
      );
    },
    canCreateKanbanLead() {
      return (
        this.hasKanbanBoard && !!this.contactId && !this.isSavingKanbanLead
      );
    },
  },
  watch: {
    'currentChat.id': {
      immediate: true,
      handler() {
        this.fetchKanbanLead();
      },
    },
  },
  methods: {
    async fetchKanbanLead() {
      if (!this.currentChat?.id) return;

      this.isLoadingKanbanLead = true;
      try {
        const board = await this.resolveKanbanBoard();
        if (!board) {
          this.kanbanBoard = null;
          this.kanbanDeal = null;
          this.kanbanStages = [];
          return;
        }

        const { data } = await BoardsAPI.getBoard(board.id);
        this.kanbanBoard = data;
        this.kanbanStages = data.stages || [];
        this.kanbanDeal = this.findConversationDeal(data);
      } catch {
        useAlert(this.$t('CONVERSATION.CRM.LOAD_ERROR'));
      } finally {
        this.isLoadingKanbanLead = false;
      }
    },
    async resolveKanbanBoard() {
      const inboxId = this.currentChat?.inbox_id;
      const { data: linkedBoards } = await BoardsAPI.get({ inbox_id: inboxId });
      if (linkedBoards.length) return linkedBoards[0];

      const { data: boards } = await BoardsAPI.get();
      return boards.find(board => !board.inbox_id) || boards[0] || null;
    },
    findConversationDeal(board) {
      const deals = (board.stages || []).flatMap(stage => stage.deals || []);
      const conversationIds = [
        this.currentChat.id,
        this.currentChat.display_id,
      ].map(id => Number(id));

      return (
        deals.find(deal =>
          (deal.conversations || []).some(
            conversation =>
              conversationIds.includes(Number(conversation.id)) ||
              conversationIds.includes(Number(conversation.display_id))
          )
        ) || null
      );
    },
    leadPayload(stageId) {
      return {
        deal: {
          name: this.$t('CONVERSATION.CRM.LEAD_NAME', {
            id: this.conversationDisplayId,
            name: this.contactName,
          }),
          amount_cents: 0,
          priority: this.currentChat?.priority || 'no_priority',
          contact_id: this.contactId,
          assignee_id: this.currentChat?.meta?.assignee?.id || null,
          stage_id: stageId,
        },
      };
    },
    async createKanbanLead() {
      if (!this.kanbanBoard || !this.kanbanStages.length) {
        useAlert(this.$t('CONVERSATION.CRM.NO_BOARD'));
        return;
      }

      if (!this.contactId) {
        useAlert(this.$t('CONVERSATION.CRM.CONTACT_REQUIRED'));
        return;
      }

      this.isSavingKanbanLead = true;
      try {
        const stageId = this.kanbanStages[0].id;
        await BoardsAPI.createDeal(this.kanbanBoard.id, {
          ...this.leadPayload(stageId),
          conversation_id: this.currentChat.id,
        });
        await this.fetchKanbanLead();
        useAlert(this.$t('CONVERSATION.CRM.CREATE_SUCCESS'));
      } catch {
        useAlert(this.$t('CONVERSATION.CRM.CREATE_ERROR'));
      } finally {
        this.isSavingKanbanLead = false;
      }
    },
    async onClickLeadStage(stage) {
      if (!this.kanbanBoard || !this.kanbanDeal || !stage) return;
      if (stage.id === this.kanbanDeal.stage_id) return;

      this.isSavingKanbanLead = true;
      try {
        await BoardsAPI.moveDeal(
          this.kanbanBoard.id,
          this.kanbanDeal.id,
          stage.id,
          0
        );
        this.kanbanDeal = { ...this.kanbanDeal, stage_id: stage.id };
        useAlert(this.$t('CONVERSATION.CRM.STAGE_SUCCESS'));
      } catch {
        useAlert(this.$t('CONVERSATION.CRM.STAGE_ERROR'));
      } finally {
        this.isSavingKanbanLead = false;
      }
    },
    openKanbanBoard() {
      this.$router.push({
        name: 'kanban_board',
        query: {
          ...(this.kanbanBoard?.id ? { board_id: this.kanbanBoard.id } : {}),
        },
      });
    },
    openKanbanDeal() {
      this.$router.push({
        name: 'kanban_board',
        query: {
          board_id: this.kanbanBoard.id,
          deal_id: this.kanbanDeal.id,
        },
      });
    },
    formatCurrency(cents) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format((cents || 0) / 100);
    },
    formatDate(date) {
      if (!date) return this.$t('CONVERSATION.CRM.NO_CLOSE_DATE');
      return new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(date));
    },
    onSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    onClickAssignAgent(selectedItem) {
      if (this.assignedAgent && this.assignedAgent.id === selectedItem.id) {
        this.assignedAgent = null;
      } else {
        this.assignedAgent = selectedItem;
      }
    },

    onClickAssignTeam(selectedItemTeam) {
      if (this.assignedTeam && this.assignedTeam.id === selectedItemTeam.id) {
        this.assignedTeam = null;
      } else {
        this.assignedTeam = selectedItemTeam;
      }
    },

    onClickAssignPriority(selectedPriorityItem) {
      const isSamePriority =
        this.assignedPriority &&
        this.assignedPriority.id === selectedPriorityItem.id;

      this.assignedPriority = isSamePriority ? null : selectedPriorityItem;
    },
  },
};
</script>

<template>
  <div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
      >
        <template #button>
          <NextButton
            v-if="showSelfAssign"
            link
            xs
            icon="i-lucide-arrow-right"
            class="!gap-1"
            :label="$t('CONVERSATION_SIDEBAR.SELF_ASSIGN')"
            @click="onSelfAssign"
          />
        </template>
      </ContactDetailsItem>
      <MultiselectDropdown
        :options="agentsList"
        :selected-item="assignedAgent"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.AGENT')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.AGENT')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.AGENT')
        "
        @select="onClickAssignAgent"
      />
    </div>
    <div>
      <ContactDetailsItem
        compact
        :title="$t('CONVERSATION_SIDEBAR.TEAM_LABEL')"
      />
      <MultiselectDropdown
        :options="teamsList"
        :selected-item="assignedTeam"
        :multiselector-title="$t('AGENT_MGMT.MULTI_SELECTOR.TITLE.TEAM')"
        :multiselector-placeholder="$t('AGENT_MGMT.MULTI_SELECTOR.PLACEHOLDER')"
        :no-search-result="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.NO_RESULTS.TEAM')
        "
        :input-placeholder="
          $t('AGENT_MGMT.MULTI_SELECTOR.SEARCH.PLACEHOLDER.TEAM')
        "
        @select="onClickAssignTeam"
      />
    </div>
    <div>
      <ContactDetailsItem compact :title="$t('CONVERSATION.PRIORITY.TITLE')" />
      <MultiselectDropdown
        :options="priorityOptions"
        :selected-item="assignedPriority"
        :multiselector-title="$t('CONVERSATION.PRIORITY.TITLE')"
        :multiselector-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SELECT_PLACEHOLDER')
        "
        :no-search-result="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.NO_RESULTS')
        "
        :input-placeholder="
          $t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.INPUT_PLACEHOLDER')
        "
        @select="onClickAssignPriority"
      />
    </div>
    <div class="mt-4 border-t border-n-weak pt-4">
      <ContactDetailsItem compact :title="$t('CONVERSATION.CRM.TITLE')" />
      <div
        v-if="isLoadingKanbanLead"
        class="rounded-md border border-n-weak px-3 py-3 text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.CRM.LOADING') }}
      </div>
      <div v-else class="rounded-lg border border-n-weak p-3">
        <div v-if="hasKanbanDeal" class="flex flex-col gap-3">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="mb-1 text-xs font-medium uppercase text-n-slate-10">
                {{ kanbanBoard.name }}
              </p>
              <h4 class="mb-0 truncate text-sm font-semibold text-n-slate-12">
                {{ kanbanDeal.name }}
              </h4>
            </div>
            <span
              class="inline-flex max-w-[110px] items-center rounded-full border border-n-weak px-2 py-1 text-xs font-medium text-n-slate-11"
              :title="currentStageName"
            >
              <span class="truncate">{{ currentStageName }}</span>
            </span>
          </div>

          <div class="grid grid-cols-2 gap-2 text-xs">
            <div class="rounded-md border border-n-weak px-2.5 py-2">
              <span class="block text-n-slate-10">
                {{ $t('CONVERSATION.CRM.VALUE') }}
              </span>
              <strong class="mt-1 block text-sm text-n-slate-12">
                {{ dealAmount }}
              </strong>
            </div>
            <div class="rounded-md border border-n-weak px-2.5 py-2">
              <span class="block text-n-slate-10">
                {{ $t('CONVERSATION.CRM.ASSIGNEE') }}
              </span>
              <strong class="mt-1 block truncate text-sm text-n-slate-12">
                {{ dealAssigneeName }}
              </strong>
            </div>
            <div class="rounded-md border border-n-weak px-2.5 py-2">
              <span class="block text-n-slate-10">
                {{ $t('CONVERSATION.CRM.EXPECTED_CLOSE') }}
              </span>
              <strong class="mt-1 block text-sm text-n-slate-12">
                {{ expectedCloseDateLabel }}
              </strong>
            </div>
            <div class="rounded-md border border-n-weak px-2.5 py-2">
              <span class="block text-n-slate-10">
                {{ $t('CONVERSATION.CRM.CONVERSATION_STATUS') }}
              </span>
              <strong
                class="mt-1 block truncate text-sm capitalize text-n-slate-12"
              >
                {{ linkedConversationStatus }}
              </strong>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <MultiselectDropdown
              :options="leadStageOptions"
              :selected-item="selectedLeadStage"
              :multiselector-title="$t('CONVERSATION.CRM.STAGE_LABEL')"
              :multiselector-placeholder="
                $t('CONVERSATION.CRM.STAGE_PLACEHOLDER')
              "
              :no-search-result="$t('CONVERSATION.CRM.NO_STAGE_RESULTS')"
              :input-placeholder="$t('CONVERSATION.CRM.STAGE_SEARCH')"
              @select="onClickLeadStage"
            />
            <div class="grid grid-cols-2 gap-2">
              <NextButton
                ghost
                slate
                sm
                icon="i-lucide-kanban"
                class="justify-center"
                :label="$t('CONVERSATION.CRM.OPEN_BOARD')"
                @click="openKanbanBoard"
              />
              <NextButton
                solid
                blue
                sm
                icon="i-lucide-external-link"
                class="justify-center"
                :label="$t('CONVERSATION.CRM.OPEN_DEAL')"
                @click="openKanbanDeal"
              />
            </div>
          </div>
        </div>

        <div v-else class="flex flex-col gap-3">
          <div>
            <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ $t('CONVERSATION.CRM.NO_DEAL') }}
            </h4>
            <p class="mb-0 text-xs leading-5 text-n-slate-11">
              {{
                hasKanbanBoard
                  ? $t('CONVERSATION.CRM.NO_DEAL_DESCRIPTION')
                  : $t('CONVERSATION.CRM.NO_BOARD_DESCRIPTION')
              }}
            </p>
          </div>
          <NextButton
            solid
            blue
            sm
            icon="i-lucide-plus"
            class="w-full justify-center"
            :is-loading="isSavingKanbanLead"
            :disabled="!canCreateKanbanLead"
            :label="$t('CONVERSATION.CRM.CREATE')"
            @click="createKanbanLead"
          />
        </div>
      </div>
    </div>
    <ContactDetailsItem
      compact
      :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_LABELS')"
    />
    <ConversationLabels :conversation-id="conversationId" />
  </div>
</template>
